/**
 * Hardware Monitor API Client
 */

import { buildApiUrl } from "../lib/api";
import { getAuthHeaders } from "./auth";

export interface HardwareDevice {
  _id: string;
  tenantId: string;
  tenantName?: string;
  name: string;
  hardwareType: "connector" | "terminal" | "camera" | string;
  connectorName?: string;
  deviceName?: string;
  status: "online" | "offline" | "never-connected";
  lastSeen: string | null;
  lastSeenAgo: string;
  certificateExpiry?: string;
  certificateStatus: "valid" | "expiring-soon" | "expired" | "no-certificate";
  ip?: string;
  port?: number;
  location?: string;
  plant?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface HardwareStats {
  total: number;
  byType: Record<string, number>;
  byStatus: {
    online: number;
    offline: number;
    neverConnected: number;
  };
  byTenant: Record<string, number>;
  certificateIssues: {
    expired: number;
    expiringSoon: number;
  };
}

export interface HardwareMonitorResponse {
  stats: HardwareStats;
  devices: HardwareDevice[];
  timestamp: string;
}

export interface ConnectionLog {
  deviceId: string;
  deviceName: string;
  hardwareType: string;
  lastSeen: string | null;
  status: "online" | "offline";
}

export interface HardwareLogsResponse {
  tenantId: string;
  logs: ConnectionLog[];
  count: number;
}

/**
 * Get hardware monitor overview
 */
export async function getHardwareMonitor(): Promise<HardwareMonitorResponse> {
  const response = await fetch(buildApiUrl("/api/hardware-monitor"), {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch hardware monitor: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get device details
 */
export async function getDeviceDetails(deviceId: string): Promise<HardwareDevice> {
  const response = await fetch(buildApiUrl(`/api/hardware-monitor/device/${deviceId}`), {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch device details: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get connection logs for a tenant
 */
export async function getTenantLogs(tenantId: string, limit = 50): Promise<HardwareLogsResponse> {
  const response = await fetch(buildApiUrl(`/api/hardware-monitor/logs/${tenantId}?limit=${limit}`), {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch tenant logs: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create a new hardware device
 */
export interface CreateHardwareDeviceInput {
  type: "hardware" | "hardwareConnector";
  hardwareType: "connector" | "terminal";
  connectorName?: string;
  deviceName?: string;
  name?: string;
  tenantId: string;
  ip?: string;
  port?: number;
  location?: string;
  metadata?: Record<string, any>;
}

export async function createHardwareDevice(input: CreateHardwareDeviceInput): Promise<HardwareDevice> {
  const response = await fetch(buildApiUrl("/api/hardware-monitor/device"), {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw new Error(`Failed to create hardware device: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update a hardware device
 */
export interface UpdateHardwareDeviceInput {
  connectorName?: string;
  deviceName?: string;
  name?: string;
  ip?: string;
  port?: number;
  location?: string;
  status?: string;
  hardwareType?: string;
}

export async function updateHardwareDevice(deviceId: string, updates: UpdateHardwareDeviceInput): Promise<HardwareDevice> {
  const response = await fetch(buildApiUrl(`/api/hardware-monitor/device/${deviceId}`), {
    method: "PATCH",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    throw new Error(`Failed to update hardware device: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Löscht ein Hardware-Gerät
 */
export async function deleteHardwareDevice(deviceId: string): Promise<void> {
  const response = await fetch(buildApiUrl(`/api/hardware-monitor/device/${deviceId}`), {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Delete failed" }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
}
